import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { PassAlgarveLogo } from "@/components/PassAlgarveLogo";
import { useLanguage } from "@/contexts/LanguageContext";
import { Menu, X } from "lucide-react";

/**
 * Site-wide fixed header used on public pages.
 * Includes a fully working mobile drawer with all navigation items.
 *
 * Props:
 *   - onBuyClick: optional handler for "Comprar Agora" button
 *   - active: label of the currently active page (e.g., "Sobre")
 */
export default function SiteHeader({ onBuyClick, active }) {
  const { t, localizePath } = useLanguage();
  const NAV_ITEMS = [
    { label: t("nav.home"),        to: localizePath("/") },
    { label: t("nav.experiences"), to: localizePath("/#experiences") },
    { label: t("nav.how"),         to: localizePath("/como-funciona") },
    { label: t("nav.passports"),   to: localizePath("/#passport") },
    { label: t("nav.partners"),    to: localizePath("/#partners") },
    { label: t("nav.about"),       to: localizePath("/sobre") },
  ];
  const [mobileOpen, setMobileOpen] = useState(false);
  const handleBuy = () => {
    setMobileOpen(false);
    if (onBuyClick) onBuyClick();
    else window.location.href = localizePath("/#passport");
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#071A35] border-b border-white/10" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link to={localizePath("/")} className="flex items-center" data-testid="header-logo" onClick={() => setMobileOpen(false)}>
          <PassAlgarveLogo size="small" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden xl:flex items-center gap-6" data-testid="header-nav">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              className={`font-medium text-xs uppercase tracking-wider transition-colors ${
                active === item.label ? "text-[#D4A85D]" : "text-white/85 hover:text-[#D4A85D]"
              }`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-3">
          <LanguageSwitcher variant="navy" />
          <Link
            to="/login"
            className="bg-[#D4A85D] hover:bg-[#c9994a] text-[#071A35] font-bold rounded-md px-6 py-2.5 text-xs uppercase tracking-wider transition-colors"
            data-testid="header-login-btn"
          >
            {t("nav.login")}
          </Link>
        </div>

        {/* Mobile right side: language + hamburger */}
        <div className="flex xl:hidden items-center gap-2">
          <LanguageSwitcher variant="navy" />
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Fechar menu" : "Abrir menu"}
            className="text-white p-2"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="xl:hidden bg-[#071A35] border-t border-white/10 overflow-hidden"
            data-testid="mobile-drawer"
          >
            <div className="px-4 py-6 space-y-1">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className={`block w-full text-left py-3 px-2 text-sm font-medium uppercase tracking-wider border-b border-white/5 transition-colors ${
                    active === item.label ? "text-[#D4A85D]" : "text-white/85 hover:text-[#D4A85D]"
                  }`}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </Link>
              ))}
              <div className="pt-5">
                <Link
                  to="/login"
                  onClick={() => setMobileOpen(false)}
                  className="block w-full text-center bg-[#D4A85D] hover:bg-[#c9994a] text-[#071A35] font-bold rounded-md py-3.5 text-sm uppercase tracking-wider transition-colors"
                  data-testid="mobile-login-btn"
                >
                  {t("nav.login")}
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
